import { Edit2, Archive, Trash2, Copy, Calendar, Infinity as InfinityIcon, Users, User, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  useOneTimePlans, 
  useArchiveOneTimePlan, 
  useDeleteOneTimePlan,
  useOneTimePlanCalculation 
} from '@/hooks/billing/useOneTimePlans';
import { OneTimePaymentPlan, TranslationParams } from '@/types/billing';
import { useTranslation } from 'react-i18next';

interface OneTimePlansListProps {
  productId: string;
  onEditPlan: (_plan: OneTimePaymentPlan) => void;
  defaultCurrency?: string;
  planType?: 'individual' | 'teams';
  currentLanguage?: string;
  showTranslations?: boolean;
}

export function OneTimePlansList({ 
  productId, 
  onEditPlan,
  defaultCurrency = 'USD',
  planType,
  currentLanguage = 'en',
  showTranslations = false
}: OneTimePlansListProps) {
  const { t } = useTranslation();
  // Create filters object - map planType to the correct filter property
  const filters = planType ? { type: planType } : {};
  
  // Build translation parameters
  const translationParams: TranslationParams = {
    lang: currentLanguage,
    includeTranslations: showTranslations ? true : currentLanguage !== 'en' ? [currentLanguage] : undefined
  };
  
  // Fetch one-time plans with translation support
  const { 
    data: allPlans = [], 
    isLoading, 
    error 
  } = useOneTimePlans(
    productId,
    filters,
    { field: 'name' },
    translationParams
  );

  // Client-side filter by plan type if backend filtering is not working
  const plans = planType 
    ? allPlans.filter(plan => {
        console.log('Filtering plan:', plan.name, 'type:', plan.type, 'looking for:', planType === 'teams' ? 'teams' : 'individual');
        return plan.type === (planType === 'teams' ? 'teams' : 'individual');
      })
    : allPlans;
    
  console.log('OneTimePlansList - planType:', planType, 'allPlans count:', allPlans.length, 'filtered plans count:', plans.length);

  // Archive and delete mutations
  const archiveMutation = useArchiveOneTimePlan();
  const deleteMutation = useDeleteOneTimePlan();

  // Calculation helpers
  const { formatAccessDuration, calculateMonthlyEquivalent } = useOneTimePlanCalculation();

  const handleArchivePlan = (plan: OneTimePaymentPlan) => {
    if (confirm(t('products.oneTimePlanList.confirmArchive', { name: plan.name }))) {
      archiveMutation.mutate({ productId, planId: plan.id });
    }
  };

  const handleDeletePlan = (plan: OneTimePaymentPlan) => {
    if (confirm(t('products.oneTimePlanList.confirmDelete', { name: plan.name }))) {
      deleteMutation.mutate({ productId, planId: plan.id });
    }
  };

  const handleDuplicatePlan = (plan: OneTimePaymentPlan) => {
    const duplicatedPlan = {
      ...plan,
      id: '',
      name: t('products.oneTimePlanList.copyName', { name: plan.name }),
      status: 'draft' as const
    };
    onEditPlan(duplicatedPlan);
  };

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'active':
        return 'default';
      case 'draft':
        return 'secondary';
      case 'archived':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const formatPrice = (price: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t('products.oneTimePlanList.loadError')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">

      {/* Loading State */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-1/2 mb-4" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : plans.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-full bg-muted p-3">
              <Calendar className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-semibold">{t('products.oneTimePlanList.emptyTitle')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('products.oneTimePlanList.emptyDescription')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const monthlyEquivalent = calculateMonthlyEquivalent(
              plan.prices?.[defaultCurrency] || 0,
              plan.accessDurationMonths
            );

            return (
              <Card key={plan.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{plan.name}</CardTitle>
                      {plan.description && (
                        <CardDescription>{plan.description}</CardDescription>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => {
                          console.log('OneTimePlansList passing plan to editor:', plan);
                          onEditPlan(plan);
                        }}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          {t('common.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicatePlan(plan)}>
                          <Copy className="h-4 w-4 mr-2" />
                          {t('common.duplicate')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {plan.status !== 'archived' && (
                          <DropdownMenuItem onClick={() => handleArchivePlan(plan)}>
                            <Archive className="h-4 w-4 mr-2" />
                            {t('common.archive')}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleDeletePlan(plan)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Pricing */}
                    <div>
                      <p className="text-2xl font-bold">
                        {formatPrice(plan.prices?.[defaultCurrency] || 0, defaultCurrency)}
                      </p>
                      {monthlyEquivalent !== null && (
                        <p className="text-sm text-muted-foreground">
                          {t('products.oneTimePlanList.monthlyEquivalent', {
                            price: formatPrice(monthlyEquivalent, defaultCurrency),
                          })}
                        </p>
                      )}
                    </div>

                    {/* Access Duration */}
                    <div className="flex items-center gap-2">
                      {plan.accessDurationMonths === 'unlimited' ? (
                        <>
                          <InfinityIcon className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-600">
                            {t('products.oneTimePlanList.lifetimeAccess')}
                          </span>
                        </>
                      ) : (
                        <>
                          <Calendar className="h-4 w-4 text-blue-600" />
                          <span className="text-sm">{formatAccessDuration(plan.accessDurationMonths)}</span>
                        </>
                      )}
                    </div>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={getStatusBadgeVariant(plan.status)}>
                        {plan.status}
                      </Badge>
                      <Badge variant="outline">
                        {plan.type === 'teams' ? (
                          <>
                            <Users className="h-3 w-3 mr-1" />
                            {t('products.oneTimePlanList.teamsLabel')}
                          </>
                        ) : (
                          <>
                            <User className="h-3 w-3 mr-1" />
                            {t('products.oneTimePlanList.individualLabel')}
                          </>
                        )}
                      </Badge>
                    </div>

                    {/* Teams info */}
                    {plan.type === 'teams' && plan.includedSeats && (
                      <div className="text-sm">
                        <span className="font-medium">
                          {t('products.oneTimePlanList.includedSeats', { count: plan.includedSeats })}
                        </span>
                      </div>
                    )}

                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
